import {Message} from 'com/gongxigames/math/core/game/message/Message';
import {Field} from 'com/gongxigames/math/core/game/message/Field';
import {Action} from 'com/gongxigames/math/core/game/enums/Action';
import {TotalBet} from 'com/gongxigames/math/core/game/TotalBet';
import {consoleLogger, DeviceType} from '@gongxigames/framework/core';
import {
    CLOSE_GAME_MESSAGE,
    GameClientWrapper,
    GameClientWrapperProperties,
    IFields,
    IGlobalEventManger,
    LogicEvents,
    ServerMessage, SlotSettings,
    WrapperUtils
} from '@gongxigames/framework/slot';
import {PPGetBalanceRequest, PPLoadGameRequest, PPPlaceBetRequest, PPRequest} from '@gongxigames/framework/integrations/pariplay/request';
import {PPLoadGameResponse, PPPlaceBetResponse, PPResponse} from '@gongxigames/framework/integrations/pariplay/response';
import {PPConfiguration} from '@gongxigames/framework/integrations/pariplay/PPConfiguration';
import {PPClientSideMessages, PPClientSideMessagesHandler} from '@gongxigames/framework/integrations/pariplay/PPClientSideMessagesHandler';
import {PPErrorsHandler} from '@gongxigames/framework/integrations/pariplay/PPErrorsHandler';
import {PP_CLIENT_WRAPPER_SETTINGS_PARAMETERS_SCHEMA, PPSettings} from '@gongxigames/framework/integrations/pariplay/PPSettings';
import {IntegrationDI} from '@gongxigames/framework/integrations/pariplay/IntegrationDI';
import {getUrlAction, URL_ACTIONS} from '@gongxigames/framework/integrations/pariplay/URLActions';
import {GameSessionPromotion} from '@gongxigames/framework/promotions';

const BINGO888_MESSAGE = {
    LOBBY: {
        data: {},
        method: 'lobby'
    },
    CASHIER: {
        data: {},
        method: 'cashier'
    }
};

export class PPGameClientWrapper extends GameClientWrapper {
    public static NAME = 'pariplay';
    public static readonly PING_BALANCE_DELAY: number = 60000;
    public static readonly REPEAT_PING_BALANCE: number = 3000;
    public static readonly STOP_PING_BALANCE_ERROR: number = 9999;
    public static readonly TEMPORARY_ERROR_MSG_CODE: number = 2;
    public static readonly NULL_RESPONSE_ERROR: number = 3;

    public config!: PPConfiguration;
    protected ppRequest!: PPRequest<PPResponse>;
    protected ppBalanceRequest!: PPGetBalanceRequest;
    protected timeoutID = -1;
    protected connectionBusy!: boolean;
    protected _clientSideMessagesHandler!: PPClientSideMessagesHandler;
    protected _errorsHandler!: PPErrorsHandler;
    protected _isGameStarted = false;
    protected _deviceType!: DeviceType;
    protected _promotionLogicInterface = new GameSessionPromotion();

    public initWrapper(properties: GameClientWrapperProperties): void {
        super.initWrapper(properties);

        const settings = this.getSettings<PPSettings>();

        settings.addParametersSchema(PP_CLIENT_WRAPPER_SETTINGS_PARAMETERS_SCHEMA);

        this.config = new PPConfiguration(settings);
        this._deviceType = settings.get('platform');
        this._currencyFormatter.setParameters({
            currencyCode: this.config.CurrencyCode,
            fractionSize: 1000,
            localeCode: settings.get('lang')
        });
        this._clientSideMessagesHandler = new PPClientSideMessagesHandler(
            this._eventManager,
            this.emitter,
            this._browser,
            this.config.GameCode,
            this.config.LanguageCode
        );
        this._installer.dependencyInjector.bindSingleton(IntegrationDI.ErrorsHandler, PPErrorsHandler);
        this._installer.dependencyInjector.bindConstant(IntegrationDI.PariPlayConfig, this.config);
        this._errorsHandler = this._installer.getComponent(IntegrationDI.ErrorsHandler);
    }

    protected setupEvents(): void {
        super.setupEvents();

        this._eventManager.on('General.Game.STARTED', this.onGameStarted.bind(this), this);
        this._eventManager.on('General.Game.INITIALIZED', this.onGameInitialized.bind(this), this);
    }

    protected onGameInitialized(): void {
        this.checkBalance();
    }

    protected onGameStarted(): void {
        this._isGameStarted = true;

        this.checkIfNLRegulationRuleNeedToApply();
    }

    protected checkIfNLRegulationRuleNeedToApply(): void {
        if (this.getSettings<SlotSettings>().get('nonMinimalBetPopupEnabled')) {
            const bets = this.getBets();
            const defaultBet = this.getDefaultBet();
            const wager = this.client.getEngine().getMaxWager();
            const {fractionSize} = this.currencyFormatter;
            const MinBetAmount = bets[0] * wager / fractionSize;
            const CurrentBetAmount = defaultBet * wager / fractionSize;

            if (MinBetAmount !== CurrentBetAmount) {
                this._clientSideMessagesHandler.onNonMinBetPopup({MinBetAmount, CurrentBetAmount});
            }
        }
    }

    protected redirectToCashier(): void {
        const cashierUrl = this.getSettings<PPSettings>().get('cashierUrl');

        this._eventManager.emit(PPClientSideMessages.CASHIER_CLICKED);

        switch (getUrlAction(cashierUrl)) {
            case URL_ACTIONS.API:
                break;
            case URL_ACTIONS.BINGO888:
                this._browser.postMessageToParent(BINGO888_MESSAGE.CASHIER, '*');
                break;
            default:
                if (cashierUrl) {
                    if (this._deviceType === DeviceType.MOBILE) {
                        this.redirect(cashierUrl);
                    } else {
                        this.openWindowTab(cashierUrl);
                    }
                }
                break;
        }
    }

    protected redirectToHomeUrl(): void {
        const homeUrl = this.getSettings<PPSettings>().get('homeUrl');

        switch (getUrlAction(homeUrl)) {
            case URL_ACTIONS.BACK:
                this.goToBack();
                break;
            case URL_ACTIONS.API:
                this.showQuitGamePopup();
                break;
            case URL_ACTIONS.BINGO888:
                this._browser.postMessageToParent(BINGO888_MESSAGE.LOBBY, '*');

                this.showQuitGamePopup();
                break;
            default:
                if (homeUrl) {
                    this.redirect(homeUrl);
                } else {
                    this.showQuitGamePopup();
                }
                break;
        }
    }

    protected goToBack(): void {
        this._eventManager.emit(PPClientSideMessages.QUIT_GAME);

        window.history.go(-1);
    }

    protected redirect(url: string): void {
        this._eventManager.emit(PPClientSideMessages.QUIT_GAME);

        if (this.getSettings<PPSettings>().get('dontLoadUrlFromParent')) {
            location.href = url;
        } else if (window.top) {
            window.top.location.href = url;
        } else {
            window.location.href = url;
        }
    }

    protected sendToServer(request: Message): void {
        this.connectionBusy = true;
        this.timeoutID > 0 && this._browser.removeTimeout(this.timeoutID);

        const fields = request.getFields();
        const testSession: number[] = request.getField(Field.TEST_SESSION);
        const settings = this.getSettings<PPSettings>();

        if (testSession !== null) {
            request.addField(Field.TEST_SESSION, WrapperUtils.bytesToBase64(testSession));
        }

        fields.action = WrapperUtils.getActionName(request.getAction());

        if (request.getAction() === Action.INIT) {
            this.ppRequest = new PPLoadGameRequest(this.config, this._browser, settings, this);
            this.ppRequest.load(() => {
                if (this.ppRequest.isError()) {
                    consoleLogger.error(`server error:${this.ppRequest.getRPErrorMessage()}`);
                    this.onGameServerError();
                } else {
                    const loadGameResponse = (this.ppRequest.getResponse() as PPLoadGameResponse);
                    const rpBody = this.ppRequest.getRPBody();
                    const response = WrapperUtils.parseMessage(rpBody, request.getAction());
                    const bets = loadGameResponse.getBets();
                    const voucher = loadGameResponse.getVoucher();

                    if (bets && bets.length > 0) {
                        settings.set('bets', bets);
                    }

                    this.connectionBusy = false;
                    this._currencyFormatter.currencyCode = settings.get('currency');

                    if (voucher) {
                        this.getPromotion<GameSessionPromotion>().init({voucher, next_action: rpBody.data.next_action});
                    }

                    this.onServerResponse(response);
                    this._eventManager.emit(PPClientSideMessages.GAME_LOADED, this.ppRequest.getResponse().source);
                }
            }, this.onConnectionError.bind(this), this.onSlowRequest.bind(this));
        } else {
            const totalBet: TotalBet = this._client.getSession().getTotalBet();
            const sessionBet: number = totalBet && totalBet.bet;

            this.ppRequest = new PPPlaceBetRequest(this.config, this._browser, settings, sessionBet, request, this);
            this.ppRequest.load(() => {
                if (this.ppRequest.isError()) {
                    this.onGameServerError();
                } else {
                    const response: Message = WrapperUtils.parseMessage(this.ppRequest.getRPBody(), request.getAction());
                    const placeBetResponse = (this.ppRequest.getResponse() as PPPlaceBetResponse);
                    const voucher = placeBetResponse.getVoucher();

                    this.connectionBusy = false;
                    response.addField(Field.BALANCE, (this.ppRequest as PPPlaceBetRequest).getBalance());

                    if (voucher) {
                        this.getPromotion<GameSessionPromotion>().update(voucher);
                    }

                    this.onServerResponse(response);
                    this.timeoutID = this._browser.addTimeout(this.pingBalance.bind(this), PPGameClientWrapper.PING_BALANCE_DELAY);
                    this._eventManager.emit(PPClientSideMessages.TICKET_RECEIVED, (this.ppRequest as PPPlaceBetRequest).getSource());
                }
            }, this.onConnectionError.bind(this), this.onSlowRequest.bind(this));
        }
    }

    protected onSlowRequest(): void {
        const msg: ServerMessage = new ServerMessage();

        msg.topic = ServerMessage.TOPIC_CONNECTION_SLOW;
        super.callBack(msg);
    }

    protected onGameServerError(): void {
        const msg: ServerMessage = new ServerMessage();

        msg.topic = ServerMessage.TOPIC_GAMESERVER_ERROR;
        msg.data = this.ppRequest.getRPErrorMessage();

        super.callBack(msg);
    }

    protected onConnectionError(xhr?: any, isTimeout?: boolean): void {
        const msg: ServerMessage = new ServerMessage();

        if (xhr && xhr.Status) {
            msg.data = xhr.Status;
        }

        msg.topic = isTimeout ? ServerMessage.TOPIC_CONNECTION_TIMEOUT : ServerMessage.TOPIC_CONNECTION_ERROR;

        super.callBack(msg);
    }

    protected checkBalance(): void {
        const balance = (this.ppRequest as PPLoadGameRequest).getBalance();

        if (!balance || balance < 0) {
            this.pingBalance();
        } else {
            this.timeoutID > 0 && this._browser.removeTimeout(this.timeoutID);
            this.timeoutID = this._browser.addTimeout(this.pingBalance.bind(this), PPGameClientWrapper.PING_BALANCE_DELAY);
        }
    }

    protected pingBalance(): void {
        if (this.connectionBusy || !this.config.BalanceURL) {
            return;
        }
        this.ppBalanceRequest = new PPGetBalanceRequest(this.config, this._browser);
        this.ppBalanceRequest.load(this.pingBalanceCallback.bind(this), this.onPingBalanceServerError.bind(this), null!);
    }

    protected pingBalanceCallback(xhr: any): void {
        if (this.connectionBusy) {
            return;
        }

        this.timeoutID > 0 && this._browser.removeTimeout(this.timeoutID);

        const msg: ServerMessage = this.ppBalanceRequest.getBalanceMessage();

        if (xhr && xhr.Status) {
            msg.data = xhr.Status;
        }

        msg.topic = ServerMessage.TOPIC_BALANCEUPDATE;

        this.timeoutID = this._browser.addTimeout(this.pingBalance.bind(this), PPGameClientWrapper.PING_BALANCE_DELAY);

        this.ppBalanceRequest.process(this, msg);

        super.callBack(msg);
    }

    protected onPingBalanceServerError(xhr: any): void {
        if (this.connectionBusy) {
            return;
        }

        this.timeoutID > 0 && this._browser.removeTimeout(this.timeoutID);

        const msg: ServerMessage = new ServerMessage();

        msg.topic = ServerMessage.TOPIC_BALANCEUPDATE;

        if (xhr && xhr.Status) {
            msg.data = xhr.Status;
        }

        if (this.ppBalanceRequest.getErrorCode() && this.ppBalanceRequest.getErrorCode() !== PPGameClientWrapper.NULL_RESPONSE_ERROR) {
            if (this.ppBalanceRequest.getErrorCode() !== PPGameClientWrapper.STOP_PING_BALANCE_ERROR) {
                this.timeoutID = this._browser.addTimeout(this.pingBalance.bind(this), PPGameClientWrapper.REPEAT_PING_BALANCE);
            }

            super.callBack(msg);
        }
    }

    protected callBack(response: ServerMessage): void {
        const msgs: ServerMessage[] = this.ppRequest.process(this, response);

        for (const msg of msgs) {
            super.callBack(msg);
        }
    }

    public responseHandle(response: ServerMessage): void {
        switch (response.topic) {
            case ServerMessage.TOPIC_BALANCEUPDATE: {
                this.handleBalance(response);
                break;
            }
            case ServerMessage.TOPIC_CONNECTION_ERROR: {
                this.handleConnectionErrors(response);
                break;
            }
            default: {

                return;
            }
        }
    }

    public freeSpin(fields?: IFields): void {
        const message = this.createMessage(Action.FREESPIN, fields!);

        this.handleMessage(message);
    }

    public respin(fields?: IFields): void {
        const message = this.createMessage(Action.RESPIN, fields!);

        this.handleMessage(message);
    }

    public minispin(fields?: IFields): void {
        // @ts-ignore TODO: Refactoring required
        const message = this.createMessage(Action.MINISPIN, fields!);

        this.handleMessage(message);
    }

    public pick(index: number, fields?: IFields): void {
        const message = this.createMessage(Action.PICK, {
            ...fields,
            [Field.INDEX]: index
        });

        this.handleMessage(message);
    }

    public select(index: number, fields?: IFields): void {
        const message = this.createMessage(Action.SELECT, {
            ...fields,
            [Field.INDEX]: index
        });

        this.handleMessage(message);
    }

    protected handleConnectionErrors(message: ServerMessage): void {
        if (message.data) {
            if (message.data.ErrType === PPGameClientWrapper.TEMPORARY_ERROR_MSG_CODE) {
                this._eventManager.emit(LogicEvents.TEMPORARY_ERROR, message.data);
            } else {
                this._eventManager.emit(LogicEvents.CONNECTION_ERROR, message.data);
            }
        } else {
            if (this._isGameStarted) {
                this._eventManager.emit(LogicEvents.PLACE_BET_ERROR);
            } else {
                this._eventManager.emit(LogicEvents.GAME_UNAVAILABLE);
            }
        }
    }

    protected handleBalance(message: ServerMessage): void {
        if (this.isValidBalance(message)) {
            const {data} = message;
            const balance = data.Balance !== undefined ? data.Balance : data.balance;

            this._eventManager.emit(LogicEvents.BALANCE_UPDATED, balance);
        } else if (this.isInvalidBalance(message)) {
            this._eventManager.emit(LogicEvents.INVALID_BALANCE);
        } else {
            this._eventManager.emit(LogicEvents.GAMESERVER_ERROR, {
                ErrMessageID: 'error1'
            });
        }
    }

    protected isValidBalance({data}: ServerMessage): boolean {
        return (data && data.hasOwnProperty('balance') && data.balance >= 0) ||
            (data && data.hasOwnProperty('Balance') && data.Balance >= 0);
    }

    protected isInvalidBalance({data}: ServerMessage): boolean {
        return (data && data.hasOwnProperty('balance') && data.balance < 0) ||
            (data && data.hasOwnProperty('Balance') && data.Balance < 0);
    }

    protected showQuitGamePopup(): void {
        this._eventManager.emit(PPClientSideMessages.QUIT_GAME);
        (this._eventManager as IGlobalEventManger).emit('General.Message.SHOW', CLOSE_GAME_MESSAGE);
    }

    public getDefaultBet(): number {
        const bets = this.getBets();
        const DefaultBet = this.config.DefaultBet;

        if (DefaultBet && bets.indexOf(DefaultBet) > -1) {
            return DefaultBet;
        }

        return bets[0];
    }
}
